/**
 * Helper utilities to build word highlight maps from candidate texts
 */

import { buildWordColorMap } from './wordLevelHighlighter';
import { blendColors } from './colorBlending';

/**
 * Build a comprehensive word -> color map for synthesis highlighting
 * Handles color blending when multiple sources match the same word
 */
export function buildSynthesisWordHighlightMap(
  candidateTextsWithColors: Array<{ text: string; color: string }>
): Map<string, { color: string; matchCount: number }> {
  // First pass: collect all colors for each word
  const wordToColors = new Map<string, string[]>();
  
  for (const source of candidateTextsWithColors) {
    const wordMap = buildWordColorMap(source.text, source.color);
    
    wordMap.forEach((color, word) => {
      if (!wordToColors.has(word)) {
        wordToColors.set(word, []);
      }
      wordToColors.get(word)!.push(color);
    });
  }
  
  // Second pass: blend colors for words with multiple matches
  const result = new Map<string, { color: string; matchCount: number }>();
  
  wordToColors.forEach((colors, word) => {
    // Remove duplicates (same model shouldn't contribute twice)
    const uniqueColors = Array.from(new Set(colors));
    
    result.set(word, {
      color: blendColors(uniqueColors),
      matchCount: uniqueColors.length,
    });
  });
  
  return result;
}

/**
 * Build word -> gray map for candidate highlighting (gray out ionoff overlaps)
 * This inverted approach makes UNIQUE words stand out in black
 */
export function buildCandidateWordHighlightMap(
  synthesisText: string,
  ionoffColor: string
): Map<string, { color: string; matchCount: number }> {
  const wordMap = buildWordColorMap(synthesisText, ionoffColor);
  const result = new Map<string, { color: string; matchCount: number }>();
  
  // Use gray color for matching words (to de-emphasize them)
  wordMap.forEach((color, word) => {
    result.set(word, { 
      color: '#888888', // Gray out matching words
      matchCount: 1 
    });
  });
  
  return result;
}

